import { useEffect, useState, useRef } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { CheckCircle2, Calendar, FileUp, Crown, Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { MbtiBadge } from "@/lib/mbti";
import ConversionSummary from "@/components/ConversionSummary";
import ClubMeetingScheduler from "@/components/ClubMeetingScheduler";

const WEEKDAYS = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];

export default function CoachAttendance() {
  const { user } = useAuth();
  const [players, setPlayers] = useState([]);
  const [entriesByPid, setEntriesByPid] = useState({});
  const [settings, setSettings] = useState({ current_week: 1, weeks_total: 10 });
  const [week, setWeek] = useState(1);
  const [busy, setBusy] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);
  const [captain, setCaptain] = useState(null);
  const bulkRef = useRef(null);

  const loadCaptain = (w) =>
    api.get(`/coach/captain?week=${w}`).then((r) => setCaptain((r.data && r.data[0]) || null));

  const setCaptainFor = async (player_id) => {
    try {
      if (!player_id) {
        await api.delete(`/coach/captain?club=${encodeURIComponent(user?.club)}&week=${week}`);
        toast.success("Capitão removido");
      } else {
        await api.post("/coach/captain", { club: user?.club, week_number: week, player_id });
        toast.success("Capitão nomeado");
      }
      loadCaptain(week);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleBulkVia = async (files) => {
    if (!files || files.length === 0) return;
    setBulkBusy(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("files", f));
      const { data } = await api.post("/coach/via-import-bulk", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      toast.success(`Importação VIA: ${data.success}/${data.total} sucesso · ${data.failed} falhas`);
      const failed = data.results.filter((r) => !r.ok);
      if (failed.length) {
        console.warn("VIA bulk failures:", failed);
        toast.error(`Falhas: ${failed.map((r) => `${r.filename}: ${r.error}`).slice(0, 3).join("; ")}`);
      }
      // Refresh players
      const r2 = await api.get("/coach/players");
      setPlayers(r2.data);
    } catch (err) { toast.error(formatApiError(err)); }
    setBulkBusy(false);
    if (bulkRef.current) bulkRef.current.value = "";
  };

  useEffect(() => {
    api.get("/settings").then((r) => { setSettings(r.data); setWeek(r.data.current_week_computed || r.data.current_week || 1); });
    api.get("/coach/players").then((r) => setPlayers(r.data));
  }, []);

  useEffect(() => {
    api.get("/entries?week=" + week).then((r) => {
      const map = {};
      r.data.forEach((e) => { map[e.player_id] = e; });
      setEntriesByPid(map);
    });
    loadCaptain(week);
  }, [week]);

  const save = async (player_id, partial) => {
    const current = entriesByPid[player_id] || {};
    const payload = {
      player_id,
      week_number: week,
      presente: partial.presente ?? current.presente ?? true,
      pontual: partial.pontual ?? current.pontual ?? true,
      tpc: partial.tpc ?? current.tpc ?? true,
    };
    setBusy((b) => ({ ...b, [player_id]: true }));
    try {
      const { data } = await api.post("/entries/attendance", payload);
      setEntriesByPid((m) => ({ ...m, [player_id]: data }));
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy((b) => ({ ...b, [player_id]: false }));
  };

  return (
    <div className="space-y-8 kw-fade-up" data-testid="coach-attendance-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="kw-overline mb-2">Coach · {user?.club}</div>
          <h1 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter">
            Presenças & <span className="text-[#E50000]">Reuniões</span>
          </h1>
          <p className="text-[#A3A3A3] mt-2 text-sm max-w-2xl">
            Regista presença, pontualidade e TPC para os consultores do clube <strong>{user?.club}</strong>. O <strong>Reporting</strong> e os <strong>Growth Shares</strong> são agora automáticos com base nos prazos e nas recomendações.
          </p>
        </div>
        <div className="kw-card px-5 py-3">
          <label className="kw-overline block mb-1">Semana</label>
          <input type="number" min="1" max={settings.weeks_total} className="kw-input !w-24"
            value={week} onChange={(e) => setWeek(Number(e.target.value))} data-testid="coach-week" />
        </div>
      </div>

      {/* Capitão da Semana */}
      <div className="kw-card p-5" data-testid="captain-panel">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Crown className="w-5 h-5 text-[#E50000]" />
            <div>
              <div className="kw-overline">Capitão da Semana {week}</div>
              <div className="text-xs text-[#737373]">Nomeia 1 consultor para registar presenças do clube se não puderes estar.</div>
            </div>
          </div>
          <select className="kw-input md:w-72" value={captain?.player_id || ""}
            onChange={(e) => setCaptainFor(e.target.value)} data-testid="captain-select">
            <option value="">— Sem capitão —</option>
            {players.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        {captain && (
          <div className="text-sm text-[#A3A3A3]">
            <span className="font-bold text-white">{captain.player_name}</span> é o capitão desta semana e pode registar presença, pontualidade e TPC do clube.
          </div>
        )}
      </div>

      {/* Reunião do clube */}
      <ClubMeetingScheduler club={user?.club} weeksTotal={settings.weeks_total} currentWeek={week} mode={user?.role === "admin" ? "admin" : "coach"} />

      <div className="kw-card p-4 flex flex-wrap items-center gap-3" data-testid="coach-bulk-via">
        <FileUp className="w-5 h-5 text-[#E50000]" />
        <div className="flex-1 min-w-0">
          <div className="kw-overline">Importar relatórios VIA-IS em massa</div>
          <div className="text-xs text-[#737373]">
            Seleciona vários PDFs. <strong>Renomeia cada PDF com o nome do consultor</strong> (ex: <code>Paulo Galvao.pdf</code>) para auto-match.
          </div>
        </div>
        <input ref={bulkRef} type="file" accept="application/pdf" multiple className="hidden"
          onChange={(e) => handleBulkVia(e.target.files)} data-testid="coach-bulk-input" />
        <button onClick={() => bulkRef.current?.click()} disabled={bulkBusy}
          className="kw-btn-secondary inline-flex items-center gap-2" data-testid="coach-bulk-btn">
          <FileUp className="w-4 h-4" /> {bulkBusy ? "A processar…" : "Importar PDFs"}
        </button>
      </div>

      <ConversionSummary highlightClub={user?.club} compact />

      {players.length === 0 ? (
        <div className="kw-card p-8 text-center text-[#737373]">
          Não há consultores atribuídos ao clube <strong>{user?.club}</strong>. Atribui o perfil MBTI dos consultores na área de Consultores (admin).
        </div>
      ) : (
        <div className="kw-card overflow-x-auto">
          <table className="w-full text-left text-sm min-w-[760px]" data-testid="coach-table">
            <thead className="kw-thead-sticky border-b border-[#222]">
              <tr className="kw-overline">
                <th className="p-3 kw-th-sticky-col">Consultor</th>
                <th className="p-3">Perfil</th>
                <th className="p-3">Presente?</th>
                <th className="p-3">Pontual?</th>
                <th className="p-3">TPC</th>
                <th className="p-3 text-right">Estado</th>
              </tr>
            </thead>
            <tbody>
              {players.map((p) => {
                const e = entriesByPid[p.id];
                const has = !!e;
                return (
                  <tr key={p.id} className="border-b border-[#1A1A1A] hover:bg-[#141414] bg-[#1A1A1A]">
                    <td className="p-3 font-semibold kw-col-sticky">{p.name}</td>
                    <td className="p-3"><MbtiBadge mbti={p.mbti} label={p.personality_label} compact /></td>
                    <td className="p-3">
                      <SimpleToggle value={has ? e.presente : true} onChange={(v) => save(p.id, { presente: v })} testid={`presente-${p.id}`} />
                    </td>
                    <td className="p-3">
                      <SimpleToggle value={has ? e.pontual : true} onChange={(v) => save(p.id, { pontual: v })} testid={`pontual-${p.id}`} />
                    </td>
                    <td className="p-3">
                      <SimpleToggle value={has ? e.tpc : true} onChange={(v) => save(p.id, { tpc: v })} testid={`tpc-${p.id}`} />
                    </td>
                    <td className="p-3 text-right text-xs">
                      {busy[p.id] ? <span className="text-[#A3A3A3]">A guardar…</span> :
                        has ? <span className="text-[#00FF88] inline-flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Guardado</span> :
                        <span className="text-[#737373]">Por registar</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="kw-card p-5 flex items-start gap-3">
        <Calendar className="w-5 h-5 text-[#E50000] mt-1" />
        <div className="text-sm text-[#A3A3A3]">
          <strong>Reporting automático:</strong> A janela abre todas as segundas-feiras às 00:00 e fecha domingo às 23:59. Reportou (+5 pts) se submeter dentro do prazo, Não Reportou (-10 pts) caso contrário. <strong>Growth Shares</strong> são creditados automaticamente quando o consultor recomendado faz o seu primeiro reporting.
        </div>
      </div>
    </div>
  );
}

function SimpleToggle({ value, onChange, testid }) {
  return (
    <div className="flex gap-1" data-testid={testid}>
      <button type="button" onClick={() => onChange(true)}
        className={`px-3 py-1 text-[10px] font-bold uppercase tracking-widest border ${value ? "bg-[#E50000] border-[#E50000] text-white" : "border-[#333] text-[#A3A3A3] hover:text-white"}`}>
        Sim
      </button>
      <button type="button" onClick={() => onChange(false)}
        className={`px-3 py-1 text-[10px] font-bold uppercase tracking-widest border ${!value ? "bg-[#1A1A1A] border-white text-white" : "border-[#333] text-[#A3A3A3] hover:text-white"}`}>
        Não
      </button>
    </div>
  );
}
